import type { ReactElement, ReactNode } from 'react'

import styles from './AdminPage.module.scss'

export { styles as adminStyles }

type AdminPageHeaderProps = {
  title: string
  /** What the screen is for, in the plant's words. */
  intro?: ReactNode
  /** The screen's main actions and badges, at the far edge of the title row. */
  actions?: ReactNode
}

export const AdminPageHeader = ({ title, intro, actions }: AdminPageHeaderProps): ReactElement => (
  <header className={styles['admin-page__header']}>
    <div className={styles['admin-page__header-row']}>
      <h1 className={styles['admin-page__title']}>{title}</h1>
      {actions !== undefined && <div className={styles['admin-page__actions']}>{actions}</div>}
    </div>
    {intro !== undefined && <p className={styles['admin-page__intro']}>{intro}</p>}
  </header>
)

/**
 * Said once, at the top of a screen, instead of leaving a read-only person to
 * wonder why every button is grey: the API answers FORBIDDEN to any write of a
 * read-only session, so the buttons are disabled rather than left to fail.
 */
export const ReadOnlyNote = (): ReactElement => (
  <p className={styles['admin-page__note']}>
    Tu cuenta es de solo lectura: puedes consultar esta pantalla, pero no cambiar nada.
  </p>
)

/**
 * The fields of `draft` that differ from `original`: what an update sends. The
 * update procedures refuse a patch with no field (a 200 that changed nothing
 * would read as a successful save), so an empty result means "do not send".
 * Arrays compare by content.
 */
export const changedFields = <T extends Record<string, unknown>>(original: T, draft: T): Partial<T> => {
  const patch: Partial<T> = {}
  for (const key of Object.keys(draft) as Array<keyof T>) {
    if (JSON.stringify(draft[key]) !== JSON.stringify(original[key])) {
      patch[key] = draft[key]
    }
  }
  return patch
}

export const hasFields = (patch: Record<string, unknown>): boolean => Object.keys(patch).length > 0

/** A grams or minutes field as typed: empty, a non-negative number, or something else. */
export type ParsedNumber = { valid: true; value: number | null } | { valid: false }

// es-ES, as `formatGrams` prints it: dots group thousands, a comma is the
// decimal point. A dot anywhere else is refused rather than guessed — read as a
// decimal point, the `48.416` copied off a list would save 1000 times less.
const SPANISH_NUMBER = /^(\d{1,3}(\.\d{3})+|\d+)(,\d+)?$/

/**
 * Reads a non-negative number off a text field, written the Spanish way
 * (`48.416`, `1,5`). Empty is `null` — "no limit" for a capacity, which the API
 * takes as null.
 */
export const parseNonNegative = (text: string, { integer = false } = {}): ParsedNumber => {
  const trimmed = text.trim()
  if (trimmed === '') {
    return { valid: true, value: null }
  }
  if (!SPANISH_NUMBER.test(trimmed)) {
    return { valid: false }
  }
  const value = Number(trimmed.replace(/\./g, '').replace(',', '.'))
  if (!Number.isFinite(value) || value < 0 || (integer && !Number.isInteger(value))) {
    return { valid: false }
  }
  return { valid: true, value }
}

const GRAMS = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 })

/** `48.416 g`, or the given text for a null (no limit, no minimum). */
export const formatGrams = (grams: number | null, whenNull = 'Sin límite'): string =>
  grams === null ? whenNull : `${GRAMS.format(grams)} g`

/** A number back into a text field, with a decimal comma: '' for null. */
export const numberText = (value: number | null): string => (value === null ? '' : String(value).replace('.', ','))

const DATE_TIME = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

export const formatDateTime = (moment: Date): string => DATE_TIME.format(moment)
